import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import { IFilter, IQueryParam } from 'modelos/src';
import { fromEvent, Observable, Subject, Subscription } from 'rxjs';
import {
  IFiltroGlobal,
  NavigationService,
} from '../../../modulos/navigation/navigation.service';
import { NombreDivisionPipe } from '../../nombre-division.pipe';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
} from 'rxjs/operators';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import {
  MatMenuModule,
  MAT_MENU_DEFAULT_OPTIONS,
} from '@angular/material/menu';
import { SelectorDispositivoComponent } from '../../../standalone/selector-dispositivo/selector-dispositivo.component';

export interface IFiltroTabla {
  // Campos comunes
  filter: IFieldFilter;
  label: string; // Etiqueta para mostrar
  tipo:
    | 'select'
    | 'input'
    | 'input-number'
    | 'dateRange'
    | 'ngselect-async'
    | 'ngselect'
    | 'ngselect-dispositivo'
    | 'regexp'; // Tipo de campo a mostrar
  // Para select
  elementos?: any[]; // Arreglo de elementos (para el select)
  selectValue?: string; // Valor a seleccionar de los elementos en el select (para select)
  selectLabel?: string; // Valor a mostrar de los elementos en el select (para select)
  // Etiqueta secundaria (línea chica y gris debajo del label) para 'ngselect'.
  // Opcional: si no se define, el ngselect se muestra como siempre.
  selectSubLabel?: string;
  // Búsqueda custom para 'ngselect'. Si se define, ng-select la usa en vez de
  // filtrar solo por bindLabel (permite buscar por varios campos, ej. deveui
  // que se muestra en la sublabel). Firma de ng-select: (term, item) => boolean.
  searchFn?: (term: string, item: any) => boolean;
  multiple?: boolean; // Para select multiple
  // Claves del filtro global (menú lateral) que acotan las OPCIONES de este
  // filtro. Cuando alguna de ellas cambia, el valor elegido puede quedar fuera
  // de scope (ej. un Centro Operativo que no pertenece a la nueva U.N.) y
  // dejaría el listado vacío sin explicación, así que se limpia.
  // Ver aplicarFiltroGlobal().
  dependeDeGlobal?: (keyof IFiltroGlobal)[];
  // Para dateRange
  desde?: string;
  hasta?: string;
  // Para ngselect-async
  asyncFunction?: string;
  searchOn?: string[];
  populate?: string;
}

export interface IRegExpSearch {
  fields: string[];
  value?: string;
}

export interface IFieldFilter {
  field: string;
  value?: any;
}

@Component({
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule,
    NgSelectModule,
    SelectorDispositivoComponent,
    NombreDivisionPipe,
  ],
  selector: 'app-filtro',
  templateUrl: './filtro.component.html',
  styleUrls: ['./filtro.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'shadow' },
    },
  ],
})
export class FiltroComponent implements OnInit, AfterViewInit, OnDestroy {
  // @ViewChild('inputSearch', { static: false }) inputSearch?: ElementRef;
  @ViewChildren('inputSearch') inputSearch?: ElementRef[];
  @Input() filtros: IFiltroTabla[] = [];
  @Input() search?: IRegExpSearch;
  @Input() saveName?: string;

  @Input() query!: IQueryParam;
  @Output() queryChange = new EventEmitter<IQueryParam>();

  public asyncData$?: Observable<any[]>[] = [];
  public asyncInput$?: Subject<string>[] = [];

  public filtroActivo: boolean = false;

  // Filtro global (menú lateral): oculta los filtros de tabla equivalentes
  private filtroGlobal: IFiltroGlobal = {};
  private filtroGlobal$?: Subscription;
  // field del filtro de tabla -> campo del filtro global que lo vuelve redundante
  private readonly ocultarPorGlobal: {
    [field: string]: keyof IFiltroGlobal;
  } = {
    division: 'division',
    idUnidadNegocio: 'unidadNegocio',
  };

  public isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay(),
    );

  constructor(
    private breakpointObserver: BreakpointObserver,
    private navigationService: NavigationService,
  ) {}

  // Filtros que se muestran (excluye los cubiertos por el filtro global)
  public get filtrosVisibles(): IFiltroTabla[] {
    return this.filtros.filter((dato) => !this.estaOcultoPorGlobal(dato));
  }

  // true si el filtro de tabla ya está aplicado globalmente desde el menú lateral
  public estaOcultoPorGlobal(dato: IFiltroTabla): boolean {
    const global = this.ocultarPorGlobal[dato?.filter?.field];
    return !!global && !!this.filtroGlobal?.[global];
  }

  // Claves del filtro global cuyo valor cambió entre `anterior` y `actual`.
  // La U.N. se compara por _id (el objeto se reconstruye en cada emisión).
  private clavesGlobalesQueCambiaron(
    anterior: IFiltroGlobal,
    actual: IFiltroGlobal,
  ): Set<keyof IFiltroGlobal> {
    const cambiaron = new Set<keyof IFiltroGlobal>();
    if (anterior.unidadNegocio?._id !== actual.unidadNegocio?._id) {
      cambiaron.add('unidadNegocio');
    }
    if (anterior.division !== actual.division) {
      cambiaron.add('division');
    }
    return cambiaron;
  }

  private tieneValor(dato: IFiltroTabla): boolean {
    return dato.filter.value !== undefined || !!dato.desde || !!dato.hasta;
  }

  private aplicarFiltroGlobal(filtro?: IFiltroGlobal): void {
    const anterior = this.filtroGlobal;
    this.filtroGlobal = filtro || {};
    const cambiaron = this.clavesGlobalesQueCambiaron(
      anterior,
      this.filtroGlobal,
    );

    // Limpia el valor de los filtros que quedaron ocultos (los aplica el filtro
    // global) y de los que dependen de una clave global que cambió: sus
    // opciones ya no son las mismas y el valor elegido puede no existir en el
    // nuevo scope.
    let cambiado = false;
    this.filtros.forEach((dato) => {
      // Solo si el global pasó a estar acotado: cuando se limpia, las opciones
      // se amplían y el valor elegido sigue siendo válido.
      const fueraDeScope = !!dato.dependeDeGlobal?.some(
        (clave) => cambiaron.has(clave) && !!this.filtroGlobal[clave],
      );
      if (
        (this.estaOcultoPorGlobal(dato) || fueraDeScope) &&
        this.tieneValor(dato)
      ) {
        dato.filter.value = undefined;
        dato.desde = undefined;
        dato.hasta = undefined;
        cambiado = true;
      }
    });
    if (cambiado) {
      this.cambioFiltro();
    }
  }

  private crearQuery(): IFilter<any> {
    const filters: IFilter<any> = {
      $and: [],
    };
    this.filtros.forEach((dato) => {
      // Oculto por filtro global: no aporta al query (lo aplica el filtro global)
      if (this.estaOcultoPorGlobal(dato)) {
        return;
      }
      if (
        dato.tipo === 'input' ||
        dato.tipo === 'select' ||
        dato.tipo === 'ngselect-async' ||
        dato.tipo === 'ngselect' ||
        dato.tipo === 'ngselect-dispositivo'
      ) {
        if (dato.filter.value && dato.filter.field) {
          // Si es un array (selección múltiple), usar $in operator
          if (
            Array.isArray(dato.filter.value) &&
            dato.filter.value.length > 0
          ) {
            filters.$and!.push({
              [dato.filter.field]: { $in: dato.filter.value },
            });
          } else if (!Array.isArray(dato.filter.value)) {
            // Si es un valor único, usar el valor directamente
            filters.$and!.push({ [dato.filter.field]: dato.filter.value });
          }
        }
      } else if (dato.tipo === 'dateRange') {
        if (dato.filter.field && (dato.desde || dato.hasta)) {
          const filtroFecha: { $and: object[] } = {
            $and: [],
          };
          if (dato.desde) {
            const date = new Date(dato.desde);
            date.setHours(0, 0, 0, 0);
            filtroFecha.$and.push({
              [dato.filter.field]: { $gte: date.toISOString() },
            });
          }
          if (dato.hasta) {
            const date = new Date(dato.hasta);
            date.setHours(23, 59, 59, 999);
            filtroFecha.$and.push({
              [dato.filter.field]: { $lte: date.toISOString() },
            });
          }
          filters.$and!.push(filtroFecha as any);
        }
      } else if (dato.tipo === 'regexp') {
        if (dato.filter.field && dato.filter.value) {
          filters.$and!.push({
            [dato.filter.field]: { $regex: dato.filter.value, $options: 'i' },
          });
        }
      }
    });
    if (this.search?.value) {
      const search: any = {
        $or: [],
      };
      for (const field of this.search.fields) {
        search.$or.push({
          [field]: { $regex: this.search.value, $options: 'i' },
        });
      }
      filters.$and!.push(search);
    }
    if (filters.$and!.length === 0) {
      delete filters.$and;
    }
    return filters;
  }

  public updatefiltroActivo() {
    const selected = this.filtros.find(
      (dato) =>
        !this.estaOcultoPorGlobal(dato) &&
        ((Array.isArray(dato.filter.value) && dato.filter.value.length > 0) ||
          (!Array.isArray(dato.filter.value) && dato.filter.value) ||
          dato.desde ||
          dato.hasta),
    );
    this.filtroActivo = selected ? true : false;
  }

  public async cambioFiltro() {
    this.updatefiltroActivo();
    const filter = this.crearQuery();
    this.query.filter = JSON.stringify(filter);
    this.saveFilter(filter);
    this.queryChange.emit(this.query);
  }

  public async limpiarFiltros() {
    this.filtros.forEach((dato) => {
      dato.filter.value = undefined;
      dato.desde = undefined;
      dato.hasta = undefined;
    });
    if (this.search) {
      this.search.value = undefined;
    }
    this.cambioFiltro();
  }

  private initSearchChange(): void {
    if (this.inputSearch?.length) {
      this.inputSearch.forEach((input) => {
        fromEvent(input.nativeElement, 'keyup')
          .pipe(
            map((e: any) => e.target.value),
            debounceTime(500),
            distinctUntilChanged(),
          )
          .subscribe(() => {
            this.cambioFiltro();
          });
      });
    }
  }

  private saveFilter(filter: IFilter<any>) {
    if (this.saveName) {
      localStorage.setItem(`filter-${this.saveName}`, JSON.stringify(filter));
    }
  }

  private getSavedFilter(): IFilter<any> | undefined {
    if (this.saveName) {
      const saved = localStorage.getItem(`filter-${this.saveName}`);
      if (saved) {
        return JSON.parse(saved);
      }
    }
    return undefined;
  }

  private buscarPrimerRegex(obj: any): string | undefined {
    let resultado: string | undefined = undefined;

    function recorrer(valor: any): boolean {
      if (Array.isArray(valor)) {
        for (const item of valor) {
          if (recorrer(item)) return true;
        }
      } else if (typeof valor === 'object' && valor !== null) {
        for (const clave in valor) {
          if (clave === '$regex') {
            resultado = valor[clave];
            return true; // cortar la recursión
          }
          if (recorrer(valor[clave])) return true;
        }
      }
      return false;
    }

    recorrer(obj);
    return resultado;
  }

  // Guarda el filtro en this.filtros
  private completeFilter(filter: IFilter<any>) {
    this.filtros.forEach((dato) => {
      if (dato.filter.field && filter.$and) {
        const found = filter.$and.find(
          (f) => f[dato.filter.field] !== undefined,
        );
        if (found) {
          // Si encontramos un operador $in, significa que es selección múltiple
          const fieldValue = found[dato.filter.field] as any;
          if (fieldValue && typeof fieldValue === 'object' && fieldValue.$in) {
            dato.filter.value = fieldValue.$in;
          } else {
            dato.filter.value = found[dato.filter.field];
          }
          this.filtroActivo = true;
        } else {
          dato.filter.value = undefined;
        }
      }
      if (dato.tipo === 'dateRange') {
        if (filter.$and) {
          const dateFilter = filter.$and.find(
            (f: any) => f[dato.filter.field] !== undefined,
          );
          if (dateFilter) {
            if ((dateFilter[dato.filter.field] as any)?.$gte) {
              dato.desde = new Date(
                (dateFilter[dato.filter.field] as any)!.$gte!,
              ).toISOString();
              this.filtroActivo = true;
            }
            if ((dateFilter[dato.filter.field] as any)?.$lte) {
              dato.hasta = new Date(
                (dateFilter[dato.filter.field] as any)!.$lte!,
              ).toISOString();
              this.filtroActivo = true;
            }
          } else {
            dato.desde = undefined;
            dato.hasta = undefined;
          }
        }
      }
    });

    if (this.search?.fields?.length) {
      const regexValue = this.buscarPrimerRegex(filter);
      if (regexValue) {
        this.search.value = regexValue;
      } else {
        this.search.value = undefined;
      }
    }
  }

  ngOnInit(): void {
    this.filtroGlobal = this.navigationService.getFiltroGlobal();
    this.filtroGlobal$ = this.navigationService
      .subscribeFiltroGlobal()
      .subscribe((filtro) => this.aplicarFiltroGlobal(filtro));
  }

  ngOnDestroy(): void {
    this.filtroGlobal$?.unsubscribe();
  }

  ngAfterViewInit() {
    this.initSearchChange();

    if (this.saveName) {
      const savedFilter = this.getSavedFilter();
      if (savedFilter) {
        this.completeFilter(savedFilter);
        this.query = this.query || {};
        this.query.filter = JSON.stringify(savedFilter);
        this.queryChange.emit(this.query);
      }
    }
  }
}
